if (!customElements.get('faq-help-center')) {
  class FaqHelpCenter extends HTMLElement {
    connectedCallback() {
      this.searchInput = this.querySelector('[data-faq-search]');
      this.searchBox = this.querySelector('[data-faq-search-box]');
      this.chrome = this.querySelector('[data-faq-chrome]');
      this.crumbs = this.querySelector('[data-faq-crumbs]');
      this.crumbCategory = this.querySelector('[data-faq-crumb-category]');
      this.crumbCategorySep = this.querySelector('[data-faq-crumb-category-sep]');
      this.crumbArticle = this.querySelector('[data-faq-crumb-article]');
      this.crumbArticleSep = this.querySelector('[data-faq-crumb-article-sep]');
      this.headingEl = this.querySelector('[data-faq-heading]');
      this.homeView = this.querySelector('[data-view="home"]');
      this.resultsView = this.querySelector('[data-view="results"]');
      this.articleView = this.querySelector('[data-view="article"]');
      this.resultsCount = this.querySelector('[data-faq-results-count]');
      this.emptyEl = this.querySelector('[data-faq-empty]');
      this.emptyTitle = this.querySelector('[data-faq-empty-title]');
      this.emptyCategories = this.querySelector('[data-faq-empty-categories]');
      this.related = this.querySelector('[data-faq-related]');
      this.relatedList = this.querySelector('[data-faq-related-list]');
      this.resultCards = this.querySelectorAll('[data-faq-result]');
      this.articles = this.querySelectorAll('[data-faq-item]');
      this.searchTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onSearchKeydown = this.onSearchKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.onSearchInput);
        this.searchInput.addEventListener('keydown', this.onSearchKeydown);
      }
      window.addEventListener('popstate', this.onPopState);
      this.restoreLayout();
      this.applyFromURL({ skipHistory: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this.onSearchInput);
        this.searchInput.removeEventListener('keydown', this.onSearchKeydown);
      }
      window.removeEventListener('popstate', this.onPopState);
    }

    onClick(event) {
      const layoutToggle = event.target.closest('[data-layout-toggle]');
      if (layoutToggle) {
        event.preventDefault();
        this.setLayout(layoutToggle.getAttribute('data-layout-toggle'));
        return;
      }

      const openHome = event.target.closest('[data-faq-open-home]');
      if (openHome) {
        event.preventDefault();
        this.showHome({ push: true });
        return;
      }

      const openCategory = event.target.closest('[data-faq-open-category]');
      if (openCategory) {
        event.preventDefault();
        if (openCategory.classList.contains('faq-help-center__crumb--current')) return;
        const handle = openCategory.getAttribute('data-faq-open-category');
        this.showCategory(handle, { push: true });
        return;
      }

      const openArticle = event.target.closest('[data-faq-open-article]');
      if (openArticle) {
        event.preventDefault();
        const handle = openArticle.getAttribute('data-faq-open-article');
        this.showArticle(handle, { push: true });
      }
    }

    onSearchInput() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => {
        const query = this.searchInput.value.trim();
        if (query === '') {
          this.showHome({ replace: true });
          return;
        }
        this.showSearch(query, { replace: true });
      }, 150);
    }

    onSearchKeydown(event) {
      if (event.key === 'Escape') {
        this.searchInput.value = '';
        this.showHome({ replace: true });
      }
    }

    onPopState() {
      this.applyFromURL({ skipHistory: true });
    }

    params() {
      return new URLSearchParams(window.location.search);
    }

    writeURL(params, options) {
      const { push, replace, skipHistory } = options || {};
      if (skipHistory) return;
      const url = new URL(window.location.href);
      url.search = params.toString();
      if (replace) {
        history.replaceState({}, '', url);
        return;
      }
      if (push) history.pushState({}, '', url);
    }

    applyFromURL(options) {
      const params = this.params();
      const article = params.get('article');
      const category = params.get('category');
      const query = params.get('q');

      if (article) {
        this.showArticle(article, options);
        return;
      }
      if (category) {
        this.showCategory(category, options);
        return;
      }
      if (query) {
        if (this.searchInput) this.searchInput.value = query;
        this.showSearch(query, options);
        return;
      }
      this.showHome(options);
    }

    restoreLayout() {
      let layout = this.dataset.layout || 'grid';
      try {
        layout = sessionStorage.getItem('faq-help-center-layout') || layout;
      } catch (error) {
        layout = this.dataset.layout || 'grid';
      }
      this.setLayout(layout);
    }

    setLayout(layout) {
      const nextLayout = layout === 'list' ? 'list' : 'grid';
      this.dataset.layout = nextLayout;
      try {
        sessionStorage.setItem('faq-help-center-layout', nextLayout);
      } catch (error) {
        // Ignore private-mode storage failures.
      }
      const buttons = this.querySelectorAll('[data-layout-toggle]');
      for (const button of buttons) {
        const active = button.getAttribute('data-layout-toggle') === nextLayout;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      }
    }

    setMode(mode) {
      this.dataset.mode = mode;
    }

    showChrome(show) {
      if (this.chrome) this.chrome.hidden = !show;
    }

    showSearchBox(show) {
      if (this.searchBox) this.searchBox.hidden = !show;
    }

    setCrumbs(options) {
      const { show, categoryName, categoryHandle, articleTitle } = options || {};
      if (this.crumbs) this.crumbs.hidden = !show;

      const showCategory = Boolean(categoryName);
      if (this.crumbCategorySep) this.crumbCategorySep.hidden = !showCategory;
      if (this.crumbCategory) {
        this.crumbCategory.hidden = !showCategory;
        this.crumbCategory.textContent = categoryName || '';
        if (categoryHandle) {
          this.crumbCategory.setAttribute('data-faq-open-category', categoryHandle);
          this.crumbCategory.setAttribute('href', this.categoryHref(categoryHandle));
        }
        const isCurrent = showCategory && !articleTitle;
        this.crumbCategory.classList.toggle('faq-help-center__crumb--current', isCurrent);
        if (isCurrent) {
          this.crumbCategory.setAttribute('aria-current', 'page');
        } else {
          this.crumbCategory.removeAttribute('aria-current');
        }
      }

      const showArticle = Boolean(articleTitle);
      if (this.crumbArticleSep) this.crumbArticleSep.hidden = !showArticle;
      if (this.crumbArticle) {
        this.crumbArticle.hidden = !showArticle;
        this.crumbArticle.textContent = articleTitle || '';
      }
    }

    showViews(name) {
      if (this.homeView) this.homeView.hidden = name !== 'home';
      if (this.resultsView) this.resultsView.hidden = name !== 'results';
      if (this.articleView) this.articleView.hidden = name !== 'article';
    }

    setHeading(text, options) {
      if (!this.headingEl) return;
      this.headingEl.textContent = text || this.dataset.heading || '';
      this.headingEl.hidden = Boolean(options && options.hidden);
    }

    setEmptySearch(isEmpty) {
      this.classList.toggle('is-empty', Boolean(isEmpty));
    }

    categoryName(handle) {
      const card = this.querySelector('.faq-help-center__card[data-faq-open-category="' + handle + '"]');
      if (card && card.getAttribute('data-category-name')) {
        return card.getAttribute('data-category-name');
      }
      const result = this.querySelector('[data-faq-result][data-category="' + handle + '"] .faq-help-center__card-meta');
      return result ? result.textContent.trim() : handle;
    }

    showHome(options) {
      if (this.searchInput) this.searchInput.value = '';
      this.setMode('home');
      this.setEmptySearch(false);
      this.setHeading(this.dataset.heading);
      this.setCrumbs({ show: false });
      this.showSearchBox(true);
      this.showChrome(true);
      this.showViews('home');
      this.writeURL(new URLSearchParams(), options || {});
    }

    showSearch(query, options) {
      const matches = this.filterResults(query, null);
      const isEmpty = matches.length === 0;
      this.setMode('search');
      this.setEmptySearch(isEmpty);
      this.setHeading(this.dataset.heading, { hidden: isEmpty });
      this.setCrumbs({ show: false });
      this.showSearchBox(true);
      this.showChrome(true);
      this.showViews('results');
      this.renderResults(matches, query);
      const params = new URLSearchParams();
      params.set('q', query);
      this.writeURL(params, options || {});
    }

    showCategory(handle, options) {
      const matches = this.filterResults('', handle);
      const categoryName = this.categoryName(handle);
      if (this.searchInput) this.searchInput.value = '';
      this.setMode('category');
      this.setEmptySearch(false);
      this.setHeading(categoryName);
      this.setCrumbs({ show: true, categoryName: categoryName, categoryHandle: handle });
      this.showSearchBox(false);
      this.showChrome(true);
      this.showViews('results');
      this.renderResults(matches, '');
      const params = new URLSearchParams();
      params.set('category', handle);
      this.writeURL(params, options || {});
    }

    showArticle(handle, options) {
      const article = this.querySelector('[data-faq-item][data-handle="' + handle + '"]');
      if (!article) {
        this.showHome(options);
        return;
      }

      const categoryHandle = article.getAttribute('data-category') || '';
      const categoryName = article.getAttribute('data-category-name') || '';
      const question = article.getAttribute('data-question') || '';

      this.setMode('article');
      this.setEmptySearch(false);
      this.setHeading('', { hidden: true });
      this.setCrumbs({
        show: true,
        categoryName: categoryName,
        categoryHandle: categoryHandle,
        articleTitle: question,
      });
      this.showSearchBox(false);
      this.showChrome(true);
      this.showViews('article');

      for (const item of this.articles) {
        item.hidden = item !== article;
      }
      this.renderRelated(handle, categoryHandle);

      const params = new URLSearchParams();
      params.set('article', handle);
      this.writeURL(params, options || {});
    }

    filterResults(query, categoryHandle) {
      const needle = (query || '').trim().toLowerCase();
      const matches = [];

      for (const card of this.resultCards) {
        const inCategory = !categoryHandle || card.getAttribute('data-category') === categoryHandle;
        if (!inCategory) {
          card.hidden = true;
          continue;
        }

        if (needle === '') {
          card.hidden = false;
          matches.push(card);
          continue;
        }

        const meta = card.querySelector('.faq-help-center__card-meta');
        const haystack = [
          card.getAttribute('data-question') || '',
          card.getAttribute('data-answer') || '',
          card.getAttribute('data-keywords') || '',
          meta ? meta.textContent : '',
        ]
          .join(' ')
          .toLowerCase();

        const matched = haystack.indexOf(needle) !== -1;
        card.hidden = !matched;
        if (matched) matches.push(card);
      }

      return matches;
    }

    renderResults(matches, query) {
      const isEmpty = matches.length === 0;
      if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
      if (this.emptyCategories) this.emptyCategories.hidden = !(isEmpty && query);
      if (this.resultsCount) this.resultsCount.hidden = isEmpty || !query;

      if (query) {
        if (this.resultsCount) {
          this.resultsCount.textContent = isEmpty ? '' : this.resultsLabel(matches.length, query);
        }
        if (this.emptyTitle) this.emptyTitle.textContent = this.noResultsLabel(query);
        return;
      }

      if (this.resultsCount) this.resultsCount.hidden = true;
      if (this.emptyTitle) this.emptyTitle.textContent = '';
    }

    renderRelated(currentHandle, categoryHandle) {
      if (!this.relatedList || !this.related) return;
      this.relatedList.replaceChildren();
      if (!categoryHandle) {
        this.related.hidden = true;
        return;
      }

      let count = 0;
      for (const card of this.resultCards) {
        if (count >= 3) break;
        if (card.getAttribute('data-category') !== categoryHandle) continue;
        if (card.getAttribute('data-handle') === currentHandle) continue;

        const relatedCard = document.createElement('a');
        relatedCard.href = this.articleHref(card.getAttribute('data-handle'));
        relatedCard.className = 'faq-help-center__card';
        relatedCard.setAttribute('data-faq-open-article', card.getAttribute('data-handle'));
        const title = document.createElement('span');
        title.className = 'faq-help-center__card-title';
        title.textContent = card.querySelector('.faq-help-center__card-title')
          ? card.querySelector('.faq-help-center__card-title').textContent
          : '';
        relatedCard.append(title);
        const chevron = this.querySelector('.icon-faq-chevron');
        if (chevron) relatedCard.append(chevron.cloneNode(true));
        this.relatedList.append(relatedCard);
        count += 1;
      }

      this.related.hidden = count === 0;
    }

    pagePath() {
      return this.dataset.faqPath || window.location.pathname;
    }

    categoryHref(handle) {
      return this.pagePath() + '?category=' + encodeURIComponent(handle || '');
    }

    articleHref(handle) {
      return this.pagePath() + '?article=' + encodeURIComponent(handle || '');
    }

    resultsLabel(count, query) {
      const template = count === 1 ? this.dataset.i18nResultsOne : this.dataset.i18nResultsOther;
      return (template || '')
        .split('__COUNT__').join(String(count))
        .split('__TERMS__').join(query);
    }

    noResultsLabel(query) {
      return (this.dataset.i18nNoResults || '').split('__TERMS__').join(query);
    }
  }

  customElements.define('faq-help-center', FaqHelpCenter);
}
